import React from 'react';
import { Link } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import { formatCategory, formatDate } from '../../utils/formatters';
import { FaArrowLeft, FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';

const BlogPost = ({ post }) => {
  // Share URLs
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post.title;

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  return (
    <div className="py-16 bg-bg-secondary min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <Link to="/blog">
            <Button variant="ghost" size="sm">
              <FaArrowLeft className="inline mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>

        {/* Blog Post */}
        <article className="max-w-4xl mx-auto">
          {/* Header */}
          <header className="mb-8">
            <div className="mb-4">
              {post.category && (
                <span className="text-accent font-body font-medium">
                  {formatCategory(post.category)}
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-secondary mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-text-primary font-body">
              {post.author && <span>By {post.author}</span>}
              {post.publishedDate && (
                <>
                  <span>•</span>
                  <span>{formatDate(post.publishedDate)}</span>
                </>
              )}
            </div>
          </header>

          {/* Cover Image */}
          {post.coverImage && (
            <div className="mb-8 rounded-lg overflow-hidden shadow-soft-lg">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <Card className="p-8 md:p-12 mb-8">
            <div
              className="prose prose-lg max-w-none font-body text-text-primary"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </Card>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-heading font-semibold text-secondary mb-3">
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-white px-4 py-2 rounded-full text-sm font-body text-text-primary shadow-soft"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share Buttons */}
          <div className="border-t border-gray-200 pt-8">
            <h3 className="text-lg font-heading font-semibold text-secondary mb-4">
              Share This Post
            </h3>
            <div className="flex gap-4">
              <a
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-smooth"
              >
                <FaFacebook />
                <span className="font-body">Facebook</span>
              </a>
              <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded hover:bg-sky-600 transition-smooth"
              >
                <FaTwitter />
                <span className="font-body">Twitter</span>
              </a>
              <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-800 transition-smooth"
              >
                <FaLinkedin />
                <span className="font-body">LinkedIn</span>
              </a>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
